import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import { DeleteOutline, Psychology as MemoryIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

interface CacheInfo {
  localStorageSize: number;
  sessionStorageSize: number;
  queryCacheCount: number;
  totalSize: number;
}

export const Settings: React.FC = () => {
  usePageTitle('Settings');
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const hasAdminPermission = user?.permissions?.includes('market_compass.admin');
  const [cacheInfo, setCacheInfo] = useState<CacheInfo>({
    localStorageSize: 0,
    sessionStorageSize: 0,
    queryCacheCount: 0,
    totalSize: 0,
  });

  const calculateCacheSize = (): CacheInfo => {
    let localStorageSize = 0;
    let sessionStorageSize = 0;

    // Calculate localStorage size
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        localStorageSize += (localStorage[key].length + key.length) * 2; // *2 for UTF-16
      }
    }

    // Calculate sessionStorage size
    for (let key in sessionStorage) {
      if (sessionStorage.hasOwnProperty(key)) {
        sessionStorageSize += (sessionStorage[key].length + key.length) * 2;
      }
    }

    // Get React Query cache count
    const queryCache = queryClient.getQueryCache();
    const queryCacheCount = queryCache.getAll().length;

    const totalSize = localStorageSize + sessionStorageSize;

    return {
      localStorageSize,
      sessionStorageSize,
      queryCacheCount,
      totalSize,
    };
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    setCacheInfo(calculateCacheSize());
  }, []);

  const handleClearCache = () => {
    try {
      // Clear React Query cache
      queryClient.clear();

      // Only clear specific application data keys (NOT authentication)
      // Remove favorites data
      const keysToRemove = ['favorites', 'userPreferences', 'appSettings'];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // Note: We intentionally do NOT clear localStorage or sessionStorage entirely
      // to preserve all authentication tokens and MSAL state

      setConfirmDialogOpen(false);

      // Recalculate cache size after clearing
      setCacheInfo(calculateCacheSize());

      enqueueSnackbar('Cache cleared successfully - React Query cache and app data removed', { variant: 'success' });
    } catch (error) {
      console.error('Error clearing cache:', error);
      enqueueSnackbar('Failed to clear cache', { variant: 'error' });
    }
  };

  const handleHardRefresh = async () => {
    try {
      // Clear React Query cache
      queryClient.clear();

      // Show notification
      enqueueSnackbar('Refreshing app and clearing cache...', { variant: 'info' });

      // Clear service worker registrations to fix blank page issues after deployments
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }

      // Small delay to allow notification to show and service workers to unregister
      setTimeout(() => {
        // Perform hard reload
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error during hard refresh:', error);
      enqueueSnackbar('Failed to refresh app', { variant: 'error' });
    }
  };

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Customize your app experience
        </Typography>
      </Box>

      <Box px={2}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Account
            </Typography>
            <Typography variant="body1">{user?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Data & Storage
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Clear all cached market data, favorites, and preferences
            </Typography>

            <Box mb={2} p={2} sx={{ backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Cache Information
              </Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Storage Size:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatBytes(cacheInfo.totalSize)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  • Local Storage:
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  {formatBytes(cacheInfo.localStorageSize)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  • Session Storage:
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  {formatBytes(cacheInfo.sessionStorageSize)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Cached Queries:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {cacheInfo.queryCacheCount}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setConfirmDialogOpen(true)}
              fullWidth
              sx={{ mb: 1 }}
            >
              Clear All Cache
            </Button>

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleHardRefresh}
              fullWidth
              sx={{
                backgroundColor: '#F5A800',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#D89500',
                },
              }}
            >
              Hard Refresh App
            </Button>
          </CardContent>
        </Card>

        {hasAdminPermission && (
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                AI Knowledge Management
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Manage Mia's knowledge base and conversation memory
              </Typography>
              <Button
                variant="contained"
                startIcon={<MemoryIcon />}
                onClick={() => navigate('/mia-knowledge')}
                fullWidth
                sx={{
                  backgroundColor: '#F5A800',
                  '&:hover': {
                    backgroundColor: '#D89600',
                  },
                }}
              >
                Manage Mia Knowledge
              </Button>
            </CardContent>
          </Card>
        )}

        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle>Clear All Cache?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will remove all cached data including market information, favorites, and preferences.
              You will remain signed in, but the app will need to reload data from the server.
            </DialogContentText>
            <Box mt={2} p={2} sx={{ backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Data to be cleared:
              </Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Storage:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatBytes(cacheInfo.totalSize)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Cached Queries:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {cacheInfo.queryCacheCount}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleClearCache} color="error" variant="contained">
              Clear Cache
            </Button>
          </DialogActions>
        </Dialog>

        <Box mt={3} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            HTL Mobile Insights v1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
